import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { CheckCircle2, Loader2, Car, ShoppingBag, UserPlus } from "lucide-react";
import { ReferralCard } from "./ReferralCard";
import { Eyebrow } from "./Brand";
import { Reveal } from "./Motion";
import { RoleToggle, VehicleChips, Field } from "./WaitlistParts";
import { API, errText } from "@/lib/api";
import axios from "axios";

const EMPTY = { name: "", email: "", zip: "", vehicle_type: "" };

export const Waitlist = ({ role, setRole, refCode, statusCode }) => {
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  useEffect(() => {
    if (!statusCode) return;
    axios.get(`${API}/waitlist/status/${statusCode}`)
      .then(({ data }) => setDone({ name: "", role: "driver", status: data }))
      .catch(() => toast.error("We couldn't find that referral code"));
  }, [statusCode]);

  const submit = async (e) => {
    e.preventDefault();
    if (role === "driver" && !form.vehicle_type) return toast.error("Pick your vehicle type");
    setBusy(true);
    try {
      const body = { ...form, role, vehicle_type: role === "driver" ? form.vehicle_type : null, ref: refCode || null };
      const { data } = await axios.post(`${API}/waitlist`, body);
      setDone({ name: form.name.split(" ")[0], role, status: data });
      setForm(EMPTY);
    } catch (err) {
      toast.error(errText(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section id="signup" className="relative py-20 sm:py-32 bg-[#F0F5FD] overflow-hidden" data-testid="waitlist-section">
      <div className="max-w-7xl mx-auto px-5 sm:px-10 grid lg:grid-cols-12 gap-12">
        <Reveal className="lg:col-span-5">
          <Eyebrow>Early access · launching soon</Eyebrow>
          <h2 className="font-display text-4xl sm:text-6xl font-extrabold tracking-tight text-[#13296B] mt-4 leading-[0.95]">Drivers first.<br />Get on the list.</h2>
          <p className="mt-6 text-slate-600 text-base sm:text-lg">The app is coming soon. Sign up for email updates and we'll tell you the moment Marketgetr goes live near you.</p>
          <div className="mt-8 rounded-2xl bg-[#13296B] text-white p-6 flex gap-4" data-testid="driver-priority-callout">
            <Car className="h-7 w-7 text-[#2F7FF0] shrink-0" />
            <p className="text-sm leading-relaxed"><strong className="font-display text-lg block mb-1">Drivers, you're our top priority.</strong>Early drivers get the first invites to onboard. Once you sign up, you'll get a personal link: invite other drivers to move up the list.</p>
          </div>
        </Reveal>

        <Reveal className="lg:col-span-7" delay={0.1}>
          <div className="relative rounded-[32px] bg-white p-6 sm:p-10 shadow-[0_30px_60px_-30px_rgba(19,41,107,0.35)] ring-1 ring-slate-200">
            <div className="absolute top-0 left-10 right-10 h-1 road-dash rounded-b" />
            {done?.role === "driver" && done.status?.referral_code ? (
              <ReferralCard name={done.name} status={done.status} />
            ) : done ? (
              <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} className="py-10 text-center" data-testid="waitlist-success-banner">
                <CheckCircle2 className="h-14 w-14 text-[#2F7FF0] mx-auto" />
                <h3 className="font-display text-3xl font-extrabold text-[#13296B] mt-5">You're on the list{done.name ? `, ${done.name}` : ""}!</h3>
                <p className="mt-3 text-slate-600 max-w-sm mx-auto">{done.role === "driver" ? "We'll email you first when driver onboarding opens in your area." : "We'll email you as soon as Marketgetr launches near you."}</p>
                <button onClick={() => setDone(null)} data-testid="waitlist-signup-another-btn" className="mt-6 text-sm font-bold text-[#2F7FF0] hover:underline">Sign up someone else</button>
              </motion.div>
            ) : (
              <form onSubmit={submit} className="space-y-6" data-testid="waitlist-form">
                {refCode && (
                  <p className="flex items-center gap-2 rounded-xl bg-[#F0F5FD] px-4 py-3 text-sm font-semibold text-[#13296B]" data-testid="waitlist-invited-note">
                    <UserPlus className="h-4 w-4 text-[#2F7FF0]" /> A Marketgetr driver invited you. Welcome!
                  </p>
                )}
                <RoleToggle role={role} setRole={setRole} />
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label="Full name" testId="waitlist-input-name" value={form.name} onChange={set("name")} required autoComplete="name" />
                  <Field label="ZIP code" testId="waitlist-input-zip" value={form.zip} onChange={set("zip")} required inputMode="numeric" maxLength={10} autoComplete="postal-code" />
                </div>
                <Field label="Email" type="email" testId="waitlist-input-email" value={form.email} onChange={set("email")} required autoComplete="email" />
                {role === "driver" && <VehicleChips value={form.vehicle_type} onChange={(v) => setForm((f) => ({ ...f, vehicle_type: v }))} />}
                <button type="submit" disabled={busy} data-testid="waitlist-submit-btn" className="w-full flex items-center justify-center gap-2 rounded-full bg-[#2F7FF0] text-white font-bold text-lg py-4 hover:bg-[#13296B] disabled:opacity-60 transition-colors duration-300">
                  {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : role === "driver" ? <Car className="h-5 w-5" /> : <ShoppingBag className="h-5 w-5" />}
                  {role === "driver" ? "Claim my driver spot" : "Notify me at launch"}
                </button>
                <p className="text-xs text-slate-500 text-center">We'll only email you about Marketgetr. Unsubscribe anytime.</p>
              </form>
            )}
          </div>
        </Reveal>
      </div>
    </section>
  );
};
